/**
 * Probe the page's WebGPU + WebAssembly state for the Information tab in
 * Settings. The result drives a "what's running where, and why" block so
 * the user can tell whether the privacy filter is on the GPU or fully on
 * the CPU, and which ops are responsible for any fallback.
 */

import { loadOrt } from "./ort-runtime.js";

export interface RuntimeStatus {
  webgpu: {
    /** `navigator.gpu` is present in this browser. */
    apiAvailable: boolean;
    /** `requestAdapter()` returned an adapter we can actually use. */
    adapterReady: boolean;
    /** Adapter description if the browser exposes one. */
    adapterName: string | null;
    /** When unavailable, a short note on why. */
    reason: string | null;
  };
  /** Execution providers we hand to ORT-Web (in order). */
  eps: readonly string[];
  /** ORT-Web's effective `numThreads`. Drives whether wasm fallback
   *  ops run single-threaded or in parallel. */
  numThreads: number;
  /** True when the page is cross-origin isolated (COOP+COEP). */
  crossOriginIsolated: boolean;
  /** SharedArrayBuffer is constructable (i.e. threading is possible). */
  sharedArrayBufferAvailable: boolean;
  /** `navigator.hardwareConcurrency`. */
  hardwareConcurrency: number;
  /** Storage state: whether the browser has granted persistent storage
   *  (so the 800 MB model in IndexedDB won't get GC'd under pressure
   *  or after weeks of no interaction), plus a usage/quota estimate. */
  storage: {
    persisted: boolean | null;     // null when the API is unavailable
    quotaBytes: number | null;     // total bytes available
    usageBytes: number | null;     // currently used by this origin
  };
  /**
   * Ops in the privacy-filter graph that ORT's partitioner spills to the
   * wasm CPU EP. With the asyncify (WebGPU) flavor we ship, every contrib
   * op the graph uses (MatMulNBits, QMoE, GatherBlockQuantized,
   * RotaryEmbedding, SkipLayerNorm, SkipSimplifiedLayerNormalization,
   * GroupQueryAttention) has a native WebGPU kernel — list stays empty.
   * With the /all (jsep) fallback flavor (CPU-only browsers), QMoE,
   * RotaryEmbedding, SkipSimplifiedLayerNormalization, and
   * GroupQueryAttention land on the CPU contrib kernels — slower but
   * correct. The list is informational; we don't currently introspect
   * the partition decisions at runtime.
   */
  cpuFallbackOps: ReadonlyArray<{ op: string; note: string }>;
}

const CPU_FALLBACK_OPS: ReadonlyArray<{ op: string; note: string }> = [];

export async function getRuntimeStatus(): Promise<RuntimeStatus> {
  const nav = navigator as unknown as {
    gpu?: { requestAdapter: (opts?: unknown) => Promise<unknown> };
  };

  // Effective numThreads — comes from whichever ORT flavor we ended up
  // loading (asyncify vs jsep). loadOrt() is memoised, so this awaits
  // pii.ts's load if it has already started, or kicks off the load now
  // if the user hit Settings → Information before running inference.
  const numThreads = (await loadOrt()).numThreads;

  const isolated = typeof self !== "undefined"
    && Boolean((self as unknown as { crossOriginIsolated?: boolean }).crossOriginIsolated);
  const sabAvailable = typeof SharedArrayBuffer !== "undefined";
  const hwc = navigator.hardwareConcurrency || 0;

  let apiAvailable = false;
  let adapterReady = false;
  let adapterName: string | null = null;
  let reason: string | null = null;

  if (!nav.gpu) {
    reason = "Your browser doesn't expose `navigator.gpu`. Chrome 113+, Edge 113+, Firefox Nightly with the dom.webgpu.enabled flag, or Safari TP all support it.";
  } else {
    apiAvailable = true;
    try {
      const adapter = (await nav.gpu.requestAdapter()) as
        | (object & { info?: { description?: string; vendor?: string; architecture?: string } })
        | null;
      if (!adapter) {
        reason = "navigator.gpu is present but `requestAdapter()` returned null — the browser has WebGPU disabled, or no compatible adapter was found.";
      } else {
        adapterReady = true;
        const info = adapter.info;
        if (info) {
          adapterName = [info.vendor, info.architecture, info.description].filter(Boolean).join(" · ") || null;
        }
      }
    } catch (e) {
      reason = `requestAdapter() threw: ${(e as Error).message ?? String(e)}`;
    }
  }

  const eps = adapterReady ? ["webgpu", "wasm"] : ["wasm"];

  // Storage probe.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const storage = (navigator as any).storage as
    | { persisted?: () => Promise<boolean>; estimate?: () => Promise<{ quota?: number; usage?: number }> }
    | undefined;
  let persisted: boolean | null = null;
  let quotaBytes: number | null = null;
  let usageBytes: number | null = null;
  if (storage?.persisted) {
    try { persisted = await storage.persisted(); } catch { /* ignore */ }
  }
  if (storage?.estimate) {
    try {
      const est = await storage.estimate();
      quotaBytes = typeof est.quota === "number" ? est.quota : null;
      usageBytes = typeof est.usage === "number" ? est.usage : null;
    } catch { /* ignore */ }
  }

  return {
    webgpu: { apiAvailable, adapterReady, adapterName, reason },
    eps,
    numThreads,
    crossOriginIsolated: isolated,
    sharedArrayBufferAvailable: sabAvailable,
    hardwareConcurrency: hwc,
    storage: { persisted, quotaBytes, usageBytes },
    cpuFallbackOps: CPU_FALLBACK_OPS,
  };
}

/**
 * Ask the browser to grant persistent storage so the cached 800 MB model
 * survives storage pressure / 7-day Safari eviction. Idempotent — calling
 * when already granted just resolves true. Returns false if the API is
 * unavailable or the browser declined. Never throws.
 *
 * Best moment to call: right after the user accepts the legal waiver and
 * before kicking off the first model download. Some browsers auto-grant
 * based on engagement heuristics; Firefox prompts; Safari silently
 * no-ops (returns false), and that's fine — the data still works, it
 * just falls under the regular eviction policy.
 */
export async function requestPersistentStorage(): Promise<boolean> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const s = (navigator as any).storage as
    | { persist?: () => Promise<boolean>; persisted?: () => Promise<boolean> }
    | undefined;
  if (!s?.persist) return false;
  try {
    if (s.persisted) {
      const already = await s.persisted();
      if (already) return true;
    }
    return await s.persist();
  } catch {
    return false;
  }
}

export function summariseEPs(s: RuntimeStatus): string {
  if (s.eps[0] === "webgpu") return "WebGPU";
  if (s.eps.length === 1 && s.eps[0] === "wasm") return "CPU with WASM";
  return s.eps.join(" → ");
}
